import { useEffect, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FINISH_ALUMINIUM, FINISH_EVO, type FastenerLight, type LockLight } from "../../../../http/lock_calculator/types";
import { applyCascade } from "../features/notices/applyCascade";
import { useNotices } from "../features/notices/NoticesContext";
import type { LockCalculatorFormValues } from "../model/form";

type Input = {
  form: UseFormReturn<LockCalculatorFormValues>;
  values: Partial<LockCalculatorFormValues> | undefined;
  locks: LockLight[];
  /** Locks compatible with connectorOne (same lock_module height), used for the connectorTwo select. */
  locksForConnectorTwo: LockLight[];
  /** Header fasteners, already filtered server-side for lock + header compatibility. */
  headerFastenerOptions: FastenerLight[];
  /** Joist fasteners, already filtered server-side for lock + joist (alpha/beta) compatibility. */
  joistFastenerOptions: FastenerLight[];
  /** Inclined fasteners, already filtered server-side for lock + header + inclined-length compatibility. */
  upliftFastenerOptions: FastenerLight[];
  isLoadingLocks: boolean;
  isLoadingLocksForConnectorTwo: boolean;
  isLoadingFasteners: boolean;
};

/** Resolves selected connector pairs and preserves existing selection normalization. */
export const useConnectorSelection = ({
  form,
  values,
  locks,
  locksForConnectorTwo,
  headerFastenerOptions,
  joistFastenerOptions,
  upliftFastenerOptions,
  isLoadingLocks,
  isLoadingLocksForConnectorTwo,
  isLoadingFasteners,
}: Input) => {
  const { reportCascade: report } = useNotices();
  const connectorOne = useMemo(
    () => locks.find((lock) => lock.id === values?.connectorPairs?.[0]?.lockItemId) ?? null,
    [locks, values?.connectorPairs?.[0]?.lockItemId],
  );
  const connectorTwo = useMemo(
    () =>
      values?.connectorPairCount === 2 && connectorOne
        ? locksForConnectorTwo.find((lock) => lock.id === values?.connectorPairs?.[1]?.lockItemId) ?? null
        : null,
    [connectorOne, locksForConnectorTwo, values?.connectorPairCount, values?.connectorPairs?.[1]?.lockItemId],
  );

  // Once a first lock is chosen, no second lock satisfies the backend's
  // pair-compatibility rules (same lock_module, and combined width fitting
  // the available header/joist space — see locksShareModule/
  // locksFitSideBySide) — a second connector pair wouldn't be assemblable,
  // so callers should disable enabling one.
  const noSecondLockAvailable = !!connectorOne && !isLoadingLocksForConnectorTwo && locksForConnectorTwo.length === 0;

  // The fastener options narrow with the chosen lock (ADR-023), so a chosen fastener can drop out of them.
  useEffect(() => {
    if (isLoadingLocks || isLoadingFasteners) return;
    const slots = [
      ["header", "fasteners.headerItemId", values?.fasteners?.headerItemId, headerFastenerOptions],
      ["joist", "fasteners.joistItemId", values?.fasteners?.joistItemId, joistFastenerOptions],
      ["inclined", "fasteners.inclinedItemId", values?.fasteners?.inclinedItemId, upliftFastenerOptions],
    ] as const;
    slots.forEach(([slot, path, chosenId, options]) => {
      if (chosenId && !options.some((item) => item.id === chosenId)) {
        applyCascade(form, report, {
          code: "fastenerCleared",
          causes: ["connectorPairs.0.lockItemId", "connectorPairs.0.finish"],
          effect: path,
          next: null,
          params: { slot },
        });
      }
    });
  }, [form, report, headerFastenerOptions, isLoadingFasteners, isLoadingLocks, joistFastenerOptions, upliftFastenerOptions, values?.fasteners?.headerItemId, values?.fasteners?.inclinedItemId, values?.fasteners?.joistItemId]);

  useEffect(() => {
    if (connectorOne && !connectorOne.evoAvailable && values?.connectorPairs?.[0]?.finish === FINISH_EVO) {
      applyCascade(form, report, {
        code: "finishForced",
        causes: ["connectorPairs.0.lockItemId"],
        effect: "connectorPairs.0.finish",
        next: FINISH_ALUMINIUM,
      });
    }
  }, [connectorOne, form, report, values?.connectorPairs?.[0]?.finish]);

  // Pair 1's finish mirrors pair 0's, falling back to ALUMINIUM when pair 0
  // is EVO but connectorTwo has no EVO variant.
  useEffect(() => {
    if (values?.connectorPairCount !== 2) return;
    const desired = values?.connectorPairs?.[0]?.finish ?? null;
    const target = desired === FINISH_EVO && (!connectorTwo || !connectorTwo.evoAvailable) ? FINISH_ALUMINIUM : desired;
    if (target != null && values?.connectorPairs?.[1]?.finish !== target) {
      // Mirroring pair 0 is silent bookkeeping; only the EVO fallback is worth telling.
      applyCascade(form, report, {
        code: "finishForced",
        causes: ["connectorPairs.1.lockItemId"],
        effect: "connectorPairs.1.finish",
        next: target,
        silent: !(desired === FINISH_EVO && target === FINISH_ALUMINIUM),
      });
    }
  }, [connectorTwo, form, report, values?.connectorPairCount, values?.connectorPairs?.[0]?.finish, values?.connectorPairs?.[1]?.finish]);

  useEffect(() => {
    const selectedId = values?.connectorPairs?.[1]?.lockItemId;
    if (isLoadingLocksForConnectorTwo || !selectedId) return;
    if (!connectorOne || !locksForConnectorTwo.some((lock) => lock.id === selectedId)) {
      applyCascade(form, report, {
        code: "secondLockCleared",
        causes: ["connectorPairs.0.lockItemId"],
        effect: "connectorPairs.1.lockItemId",
        next: null,
      });
    }
  }, [connectorOne, form, report, isLoadingLocksForConnectorTwo, locksForConnectorTwo, values?.connectorPairs?.[1]?.lockItemId]);

  useEffect(() => {
    if (noSecondLockAvailable && values?.connectorPairCount === 2) {
      applyCascade(form, report, {
        code: "pairCountReduced",
        causes: ["connectorPairs.0.lockItemId"],
        effect: "connectorPairCount",
        next: 1,
      });
    }
  }, [form, report, noSecondLockAvailable, values?.connectorPairCount]);

  return {
    connectorOne,
    connectorTwo,
    noSecondLockAvailable,
  };
};
